import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSiteContent } from '../contexts/SiteContentContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  DollarSign, 
  Brain, 
  Globe, 
  Play, 
  ArrowRight, 
  Star,
  CheckCircle,
  Sparkles,
  Heart,
  Shield
} from 'lucide-react';

// CSS-based 3D floating orbs component
const FloatingOrbs3D = ({ primaryColor, accentColor }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ perspective: '1000px' }}>
    {/* Large primary orb */}
    <div 
      className="absolute w-72 h-72 rounded-full animate-float-3d"
      style={{ 
        top: '10%', 
        right: '15%',
        background: `radial-gradient(circle at 30% 30%, ${primaryColor}60, ${primaryColor}20, transparent)`,
        boxShadow: `0 0 80px ${primaryColor}40, inset 0 0 60px ${primaryColor}20`,
        transform: 'translateZ(50px)',
        animation: 'float3d 8s ease-in-out infinite'
      }} 
    />
    {/* Medium accent orb */}
    <div 
      className="absolute w-48 h-48 rounded-full"
      style={{ 
        bottom: '20%', 
        left: '10%',
        background: `radial-gradient(circle at 30% 30%, ${accentColor}50, ${accentColor}15, transparent)`,
        boxShadow: `0 0 60px ${accentColor}30, inset 0 0 40px ${accentColor}15`,
        animation: 'float3d 10s ease-in-out infinite reverse'
      }} 
    />
    {/* Small floating orbs */}
    <div 
      className="absolute w-24 h-24 rounded-full"
      style={{ 
        top: '40%', 
        left: '25%',
        background: `radial-gradient(circle at 30% 30%, ${primaryColor}40, transparent)`,
        boxShadow: `0 0 40px ${primaryColor}25`,
        animation: 'float3d 6s ease-in-out infinite 1s'
      }} 
    />
    <div 
      className="absolute w-16 h-16 rounded-full"
      style={{ 
        bottom: '30%', 
        right: '25%',
        background: `radial-gradient(circle at 30% 30%, ${accentColor}45, transparent)`,
        boxShadow: `0 0 30px ${accentColor}20`,
        animation: 'float3d 7s ease-in-out infinite 2s'
      }} 
    />
    {/* Glowing rings */}
    <div 
      className="absolute w-96 h-96 rounded-full border-2"
      style={{ 
        top: '50%', 
        left: '50%',
        transform: 'translate(-50%, -50%) rotateX(70deg)',
        borderColor: `${primaryColor}30`,
        boxShadow: `0 0 20px ${primaryColor}20, inset 0 0 20px ${primaryColor}10`,
        animation: 'spin3d 20s linear infinite'
      }} 
    />
    <div 
      className="absolute w-80 h-80 rounded-full border"
      style={{ 
        top: '50%', 
        left: '50%',
        transform: 'translate(-50%, -50%) rotateX(70deg) rotateZ(45deg)',
        borderColor: `${accentColor}25`,
        boxShadow: `0 0 15px ${accentColor}15`,
        animation: 'spin3d 25s linear infinite reverse'
      }} 
    />
  </div>
);

// CSS-based zen wave background
const ZenWaveBackground = ({ primaryColor, accentColor }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Animated gradient waves */}
    <div 
      className="absolute inset-0"
      style={{
        background: `
          radial-gradient(ellipse at 20% 50%, ${primaryColor}15 0%, transparent 50%),
          radial-gradient(ellipse at 80% 50%, ${accentColor}15 0%, transparent 50%)
        `,
        animation: 'pulse 8s ease-in-out infinite'
      }}
    />
    {/* Floating geometric shapes */}
    <div 
      className="absolute w-32 h-32"
      style={{ 
        top: '20%', 
        left: '15%',
        background: `linear-gradient(135deg, ${primaryColor}25, transparent)`,
        clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
        animation: 'float3d 12s ease-in-out infinite'
      }} 
    />
    <div 
      className="absolute w-24 h-24"
      style={{ 
        bottom: '25%', 
        right: '20%',
        background: `linear-gradient(135deg, ${accentColor}20, transparent)`,
        clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
        animation: 'float3d 15s ease-in-out infinite 3s'
      }} 
    />
  </div>
);

const heroImages = [
  "https://images.unsplash.com/photo-1764409906975-319566e075f7?w=1920&q=80",
  "https://images.unsplash.com/photo-1602779370210-31cb71dc2ecc?w=1920&q=80"
];

const coachingImages = [
  "https://images.unsplash.com/photo-1562088287-bde35a1ea917?w=800&q=80",
  "https://images.unsplash.com/photo-1643184012410-0d0c9070e575?w=800&q=80"
];

const testimonialAvatars = [
  "https://images.unsplash.com/photo-1658288098101-84f074c292a8?w=200&q=80",
  "https://images.unsplash.com/photo-1634552516330-ab1ccc0f605e?w=200&q=80"
];

export default function LandingPage() {
  const { currentTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { getSection, getValue } = useSiteContent();

  // Get dynamic content from CMS
  const heroContent = getSection('hero');
  const statsContent = getSection('stats');
  const freedomsContent = getSection('freedoms');
  const testimonialsContent = getSection('testimonials');
  const ctaContent = getSection('cta');

  const freedoms = [
    {
      icon: DollarSign,
      title: freedomsContent.financial_title || "Financial Freedom",
      description: freedomsContent.financial_description || "Break free from financial stress. Learn strategies to build wealth, eliminate debt, and create sustainable income streams that work for you.",
      image: freedomsContent.financial_image || "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&q=80",
      features: [
        freedomsContent.financial_feature1 || "Debt elimination strategies", 
        freedomsContent.financial_feature2 || "Passive income building", 
        freedomsContent.financial_feature3 || "Smart investment basics"
      ]
    },
    {
      icon: Brain,
      title: freedomsContent.mental_title || "Mental Freedom",
      description: freedomsContent.mental_description || "Quiet the noise. Discover mindfulness techniques, stress management tools, and cognitive practices that bring clarity and peace.",
      image: freedomsContent.mental_image || coachingImages[1],
      features: [
        freedomsContent.mental_feature1 || "Mindfulness practices", 
        freedomsContent.mental_feature2 || "Stress management", 
        freedomsContent.mental_feature3 || "Emotional resilience"
      ]
    },
    {
      icon: Globe,
      title: freedomsContent.physical_title || "Physical Freedom",
      description: freedomsContent.physical_description || "Move without limits. Develop fitness routines, nutrition habits, and lifestyle changes that empower your body to thrive anywhere.",
      image: freedomsContent.physical_image || coachingImages[0],
      features: [
        freedomsContent.physical_feature1 || "Flexible fitness routines", 
        freedomsContent.physical_feature2 || "Nutrition guidance", 
        freedomsContent.physical_feature3 || "Location independence"
      ]
    }
  ];

  const testimonials = [
    {
      name: testimonialsContent.testimonial1_name || "Sarah Mitchell",
      role: testimonialsContent.testimonial1_role || "Digital Nomad",
      avatar: testimonialsContent.testimonial1_avatar || testimonialAvatars[1],
      content: testimonialsContent.testimonial1_content || "Nspire Living Healthy transformed my relationship with money and stress. I now work from anywhere in the world while feeling financially secure and mentally at peace."
    },
    {
      name: testimonialsContent.testimonial2_name || "Marcus Chen",
      role: testimonialsContent.testimonial2_role || "Entrepreneur",
      avatar: testimonialsContent.testimonial2_avatar || testimonialAvatars[0],
      content: testimonialsContent.testimonial2_content || "The holistic approach here is unmatched. I've achieved a balance I never thought possible - thriving business, clear mind, and energy to enjoy life."
    }
  ];

  // Add third testimonial if it exists
  if (testimonialsContent.testimonial3_name && testimonialsContent.testimonial3_content) {
    testimonials.push({
      name: testimonialsContent.testimonial3_name,
      role: testimonialsContent.testimonial3_role || "",
      avatar: testimonialsContent.testimonial3_avatar || "",
      content: testimonialsContent.testimonial3_content
    });
  }

  const stats = [
    { value: statsContent.stat1_value || "10K+", label: statsContent.stat1_label || "Lives Transformed" },
    { value: statsContent.stat2_value || "50+", label: statsContent.stat2_label || "Countries Reached" },
    { value: statsContent.stat3_value || "98%", label: statsContent.stat3_label || "Client Satisfaction" },
    { value: statsContent.stat4_value || "500+", label: statsContent.stat4_label || "Coaching Hours" }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroContent.background_image || heroImages[0]})` }}
        >
          <div 
            className="absolute inset-0"
            style={{ 
              background: `linear-gradient(135deg, ${currentTheme.foreground}e0 0%, ${currentTheme.foreground}80 50%, transparent 100%)`
            }}
          />
        </div>

        {/* CSS 3D Floating Orbs */}
        <FloatingOrbs3D primaryColor={currentTheme.primary} accentColor={currentTheme.accent} />

        {/* Floating Elements */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full opacity-20 animate-float" 
          style={{ backgroundColor: currentTheme.accent }} 
        />
        <div className="absolute bottom-1/4 left-1/4 w-48 h-48 rounded-full opacity-10 animate-float stagger-2" 
          style={{ backgroundColor: currentTheme.secondary }} 
        />

        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm mb-6"
                style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
              >
                <Sparkles className="w-4 h-4" />
                {heroContent.tagline || 'Your Journey to Freedom Starts Here'}
              </span>
              
              <h1 
                className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-6 text-white"
              >
                {heroContent.title_line1 || 'Transform Your Life,'}<br />
                <span style={{ color: currentTheme.secondary }}>{heroContent.title_line2 || 'Embrace Freedom'}</span>
              </h1>
              
              <p className="font-body text-lg md:text-xl font-light leading-relaxed text-white/80 mb-8 max-w-xl">
                {heroContent.subtitle || 'Discover the path to financial peace, mental clarity, and physical vitality. Our holistic coaching empowers you to break free from stress and live life on your terms.'}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to={isAuthenticated ? "/coaching" : "/register"} data-testid="hero-cta-primary">
                  <Button 
                    className="btn-primary flex items-center gap-2 text-lg"
                    style={{ backgroundColor: currentTheme.primary }}
                  >
                    {heroContent.cta_primary || 'Start Your Journey'}
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/media" data-testid="hero-cta-secondary">
                  <Button 
                    variant="outline"
                    className="btn-secondary flex items-center gap-2 text-lg border-white text-white hover:bg-white/10"
                  >
                    <Play className="w-5 h-5" />
                    {heroContent.cta_secondary || 'Watch Introduction'}
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-8 border-t border-white/20">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center md:text-left">
                    <div className="font-display text-3xl font-bold text-white">{stat.value}</div>
                    <div className="font-body text-sm text-white/60">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image Card */}
            <div className="hidden md:block relative animate-slide-up stagger-2">
              <div className="relative">
                <div 
                  className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl"
                  style={{ backgroundColor: currentTheme.accent }}
                />
                <img 
                  src={coachingImages[0]}
                  alt="Peaceful meditation"
                  className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]"
                />
                {/* Floating Badge */}
                <div 
                  className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 shadow-xl"
                  style={{ backgroundColor: `${currentTheme.background}f0` }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${currentTheme.primary}20` }}
                    >
                      <Heart className="w-6 h-6" style={{ color: currentTheme.primary }} />
                    </div>
                    <div>
                      <div className="font-display font-medium" style={{ color: currentTheme.foreground }}>
                        Join 10K+
                      </div>
                      <div className="font-body text-sm opacity-60">Happy Members</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 rounded-full bg-white/50" />
          </div>
        </div>
      </section>

      {/* Freedom Sections */}
      <section className="section-spacing relative" id="freedoms">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span 
              className="inline-block px-4 py-2 rounded-full font-body text-sm mb-4"
              style={{ backgroundColor: `${currentTheme.primary}10`, color: currentTheme.primary }}
            >
              The Three Pillars
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight mb-4" 
              style={{ color: currentTheme.foreground }}
            >
              {freedomsContent.section_title || 'Your Path to Complete Freedom'}
            </h2>
            <p className="font-body text-lg font-light leading-relaxed opacity-70">
              {freedomsContent.section_subtitle || 'True freedom comes from balance. We help you achieve harmony across all areas of your life.'}
            </p>
          </div>

          <div className="space-y-24">
            {freedoms.map((freedom, index) => (
              <div 
                key={freedom.title}
                id={freedom.title.toLowerCase().replace(' ', '-')}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <div className="relative">
                    <div 
                      className="absolute -inset-4 rounded-3xl opacity-20 blur-xl"
                      style={{ backgroundColor: currentTheme.primary }}
                    />
                    <img 
                      src={freedom.image}
                      alt={freedom.title}
                      className="relative rounded-3xl shadow-xl w-full object-cover aspect-[4/3]"
                    />
                  </div>
                </div>
                
                <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                  <div 
                    className="feature-icon mb-6"
                    style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent})` }}
                  >
                    <freedom.icon className="w-8 h-8" />
                  </div>
                  
                  <h3 className="font-display text-3xl md:text-4xl font-medium mb-4" 
                    style={{ color: currentTheme.foreground }}
                  >
                    {freedom.title}
                  </h3>
                  
                  <p className="font-body text-lg font-light leading-relaxed opacity-70 mb-6">
                    {freedom.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {freedom.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 font-body">
                        <CheckCircle className="w-5 h-5" style={{ color: currentTheme.primary }} />
                        <span style={{ color: currentTheme.foreground }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/coaching" data-testid={`freedom-cta-${index}`}>
                    <Button 
                      className="btn-primary"
                      style={{ backgroundColor: currentTheme.primary }}
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section 
        className="section-spacing relative overflow-hidden"
        style={{ backgroundColor: currentTheme.muted }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span 
              className="inline-block px-4 py-2 rounded-full font-body text-sm mb-4"
              style={{ backgroundColor: `${currentTheme.primary}10`, color: currentTheme.primary }}
            >
              Success Stories
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight mb-4" 
              style={{ color: currentTheme.foreground }}
            >
              {testimonialsContent.section_title || 'Lives Transformed'}
            </h2>
            {testimonialsContent.section_subtitle && (
              <p className="font-body text-lg font-light leading-relaxed opacity-70">
                {testimonialsContent.section_subtitle}
              </p>
            )}
          </div>

          <div className={`grid gap-8 ${testimonials.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            {testimonials.map((testimonial, index) => (
              <div 
                key={testimonial.name}
                className="testimonial-card card-hover"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  {testimonial.avatar && (
                    <img 
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <div className="font-display font-medium" style={{ color: currentTheme.foreground }}>
                      {testimonial.name}
                    </div>
                    <div className="font-body text-sm opacity-60">{testimonial.role}</div>
                  </div>
                  <div className="ml-auto flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" style={{ color: currentTheme.secondary }} />
                    ))}
                  </div>
                </div>
                <p className="font-accent text-lg italic leading-relaxed" style={{ color: currentTheme.foreground }}>
                  "{testimonial.content}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ctaContent.background_image || heroImages[1]})` }}
        >
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: `${currentTheme.foreground}e0` }}
          />
        </div>
        
        {/* CSS 3D Zen Background */}
        <ZenWaveBackground primaryColor={currentTheme.primary} accentColor={currentTheme.accent} />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10 text-center">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-8"
            style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent})` }}
          >
            <Shield className="w-10 h-10 text-white" />
          </div>
          
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight mb-4 text-white">
            {ctaContent.title || 'Ready to Transform Your Life?'}
          </h2>
          
          <p className="font-body text-lg md:text-xl font-light leading-relaxed text-white/70 mb-8 max-w-2xl mx-auto">
            {ctaContent.subtitle || 'Join thousands who have already discovered the path to true freedom. Your journey to a stress-free, fulfilling life begins today.'}
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to={isAuthenticated ? "/coaching" : "/register"} data-testid="cta-get-started">
              <Button 
                className="btn-primary text-lg"
                style={{ backgroundColor: currentTheme.secondary, color: currentTheme.foreground }}
              >
                {ctaContent.button_text || 'Get Started Free'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/media" data-testid="cta-explore">
              <Button 
                variant="outline"
                className="btn-secondary text-lg border-white text-white hover:bg-white/10"
              >
                Explore Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
